// Supervisor Today — the team-wide version of the agent's daily view:
// follow-ups due per agent, open tasks, leave, and what's been actioned.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { CalendarCheck, Loader2, Phone, AlertTriangle, MessageSquare, CheckCircle2, CalendarOff, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import UpcomingAppointmentsCard from '../components/intelligence/UpcomingAppointmentsCard';
import NotCalledCard from '../components/custom/NotCalledCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_STYLE = {
  manager_reassigned: 'bg-red-100 text-red-700',
  cooling_hot_lead: 'bg-amber-100 text-amber-700',
  stale_warm: 'bg-slate-100 text-slate-600',
};

// Terminology: some agents work "leads", the rest work "opportunities".
const OPP_AGENTS = ['edith', 'jeruz', 'liezl', 'rizza', 'phil', 'martin', 'jacob', 'joanne'];
export const termize = (label, agentName) => {
  const first = String(agentName || '').trim().toLowerCase().split(' ')[0];
  if (!OPP_AGENTS.includes(first)) return label;
  return String(label || '').replace(/lead/g, 'opportunity').replace(/Lead/g, 'Opportunity');
};

export default function SupervisorTodayPage() {
  const [data, setData] = useState(null);
  const [leave, setLeave] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [agents, setAgents] = useState([]);
  const [reassigning, setReassigning] = useState(null); // call_id being reassigned
  const [fuAgent, setFuAgent] = useState('all');
  const [err, setErr] = useState('');
  const [sp] = useSearchParams();
  const did = sp.get('domain_id');
  const dq = did ? `?domain_id=${did}` : '';

  const load = () => {
    axios.get(`${API}/analytics/supervisor/today${dq}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || 'Could not load'));
  };

  useEffect(() => {
    load();
    axios.get(`${API}/analytics/team-leave-today${dq}`).then((r) => setLeave(r.data)).catch(() => {});
    axios.get(`${API}/analytics/team-leave-upcoming${dq}`).then((r) => setUpcoming(r.data)).catch(() => {});
    axios.get(`${API}/analytics/agents/directory`).then((r) => setAgents(r.data.agents || [])).catch(() => {});
  }, []);

  const reassign = async (callId, newAgentId, fromAgentId) => {
    if (!newAgentId || newAgentId === fromAgentId) return;
    setReassigning(callId);
    try {
      await axios.post(`${API}/analytics/revival/${callId}/reassign`,
        { new_agent_id: newAgentId, note: 'Reassigned from Supervisor Today' });
      toast.success('Follow-up reassigned');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reassign failed');
    } finally {
      setReassigning(null);
    }
  };

  if (err) return <div className="p-8 text-sm text-slate-500" data-testid="supervisor-today-error">{err}</div>;
  if (!data) return <div className="p-12 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-teal-500" /></div>;

  const totalDue = data.follow_ups_by_agent.reduce((s, a) => s + a.count, 0);
  const fuAgents = data.follow_ups_by_agent.map((a) => a.agent_name);
  const fuCards = data.follow_ups_by_agent.filter((a) => fuAgent === 'all' || a.agent_name === fuAgent);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="supervisor-today-page">
      <header>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
          <CalendarCheck className="w-6 h-6 text-teal-600" /> Supervisor Today
          <span className="text-sm font-normal text-slate-500 ml-2">{data.date} · {data.timezone}</span>
        </h1>
        <p className="text-sm text-slate-500 mt-1">What your team needs to hit today — reassigned callbacks, follow-ups due, and who has already actioned what.</p>
      </header>

      <NotCalledCard domainId={did} />

      {leave?.users?.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 flex items-start gap-2" data-testid="supervisor-leave-banner">
          <CalendarOff className="w-4 h-4 mt-0.5 shrink-0" />
          <span><strong>Off today:</strong> {leave.users.map((u) => `${u.name}${u.reason === 'leave' ? ' (leave)' : ''}`).join(', ')} — reassign their callbacks to someone working.</span>
        </div>
      )}

      {upcoming?.days?.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid="supervisor-upcoming-leave">
          <p className="text-[11px] uppercase font-bold text-slate-400 mb-1.5">Upcoming leave — next 7 days</p>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            {upcoming.days.map((d) => (
              <span key={d.date} className="text-xs text-slate-600">
                <strong className="text-slate-800">{d.day}:</strong>{' '}
                {d.users.map((u) => `${u.name}${u.reason === 'leave' ? ' (leave)' : ''}`).join(', ')}
              </span>
            ))}
          </div>
        </div>
      )}

      <section data-testid="supervisor-followups-section">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h2 className="text-base font-bold text-slate-800">Follow-ups due by agent <span className="text-xs font-normal text-slate-400">({totalDue} deals)</span></h2>
          <div className="flex gap-1 flex-wrap" data-testid="followups-agent-filter">
            <button onClick={() => setFuAgent('all')}
              className={`text-[11px] font-semibold rounded-full px-2.5 py-1 ${fuAgent === 'all' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              data-testid="followups-agent-all">All</button>
            {fuAgents.map((n) => (
              <button key={n} onClick={() => setFuAgent(n)}
                className={`text-[11px] font-semibold rounded-full px-2.5 py-1 ${fuAgent === n ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                data-testid={`followups-agent-${n.replace(/\s/g, '-')}`}>{n}</button>
            ))}
          </div>
        </div>
        {fuCards.length === 0 ? (
          <p className="text-sm text-slate-400 bg-slate-50 rounded-lg px-4 py-6 text-center">Nothing due — the pipeline is clean.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {fuCards.map((a) => (
              <div key={a.agent_id} className="rounded-xl border border-slate-200 bg-white p-3.5" data-testid={`supervisor-agent-card-${a.agent_id}`}>
                <p className="font-bold text-sm text-slate-900 flex items-center justify-between">
                  {a.agent_name}
                  <span className="text-[11px] font-bold bg-teal-50 text-teal-700 rounded-full px-2 py-0.5">{a.count} due</span>
                </p>
                <ul className="mt-2 space-y-1.5">
                  {a.rows.map((r) => (
                    <li key={r.call_id} className="text-xs flex items-center gap-2 flex-wrap">
                      <span className={`px-1.5 py-0.5 rounded font-semibold ${BAND_STYLE[r.band] || 'bg-slate-100'}`}>{termize(r.label, a.agent_name)}</span>
                      <span className="font-mono text-slate-600 inline-flex items-center gap-1"><Phone className="w-3 h-3" />{r.customer_phone || '—'}</span>
                      {r.call_id && <Link to={`/calls/${r.call_id}`} className="text-teal-600 hover:underline">Open call</Link>}
                      {r.call_id && agents.length > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <UserPlus className="w-3 h-3 text-slate-400" />
                          <select
                            defaultValue=""
                            disabled={reassigning === r.call_id}
                            onChange={(e) => reassign(r.call_id, e.target.value, a.agent_id)}
                            className="text-[11px] border border-slate-200 rounded px-1 py-0.5 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-teal-500"
                            data-testid={`reassign-select-${r.call_id}`}
                          >
                            <option value="" disabled>{reassigning === r.call_id ? 'Moving…' : 'Reassign to…'}</option>
                            {agents.filter((ag) => String(ag.agent_id) !== String(a.agent_id)).map((ag) => (
                              <option key={ag.agent_id} value={ag.agent_id}>{ag.name || `Ext ${ag.agent_id}`}</option>
                            ))}
                          </select>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      <section data-testid="supervisor-appointments-section">
        <h2 className="text-base font-bold text-slate-800 mb-2">Appointments — next 24 hours</h2>
        <UpcomingAppointmentsCard days={1} />
      </section>

      {data.yesterday && (
        <section data-testid="supervisor-yesterday-section">
          <h2 className="text-base font-bold text-slate-800 mb-2">Updates from yesterday <span className="text-xs font-normal text-slate-400">({data.yesterday.date})</span></h2>
          <div className="flex flex-wrap gap-2 mb-3">
            {[['callbacks_logged', 'callbacks made'], ['sms_sent', 'SMS sent'], ['tasks_completed', 'tasks completed'],
              ['replies_received', 'replies in'], ['new_follow_ups', 'new revivable deals']].map(([k, label]) => {
              const val = data.yesterday.totals?.[k] ?? 0;
              const lw = data.yesterday.last_week?.[k];
              const diff = lw === undefined ? null : val - lw;
              return (
                <span key={k} className="text-xs font-semibold bg-white border border-slate-200 rounded-full px-3 py-1.5 text-slate-700 inline-flex items-center gap-1.5"
                  data-testid={`yesterday-stat-${k}`}>
                  <span className="text-teal-700 font-bold">{val}</span> {label}
                  {diff !== null && diff !== 0 && (
                    <span className={`text-[10px] font-bold ${diff > 0 ? 'text-emerald-600' : 'text-red-500'}`}
                      title={`vs same day last week (${lw})`}
                      data-testid={`yesterday-trend-${k}`}>
                      {diff > 0 ? '▲' : '▼'}{Math.abs(diff)}
                    </span>
                  )}
                  {diff === 0 && <span className="text-[10px] text-slate-400" title={`same as last week (${lw})`}>＝</span>}
                </span>
              );
            })}
          </div>
          <p className="text-[10px] text-slate-400 -mt-2 mb-3">▲▼ compares to the same day last week</p>
          {data.yesterday.by_agent?.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Agent</th>
                    <th className="text-right px-3 py-2 font-semibold">Callbacks</th>
                    <th className="text-right px-3 py-2 font-semibold">SMS</th>
                    <th className="text-right px-3 py-2 font-semibold">Tasks done</th>
                  </tr>
                </thead>
                <tbody>
                  {data.yesterday.by_agent.map((a) => (
                    <tr key={a.agent_id} className="border-t border-slate-100" data-testid={`yesterday-agent-${a.agent_id}`}>
                      <td className="px-3 py-2 font-semibold text-slate-800">{a.agent_name}</td>
                      <td className="px-3 py-2 text-right">{a.callbacks_logged}</td>
                      <td className="px-3 py-2 text-right">{a.sms_sent}</td>
                      <td className="px-3 py-2 text-right">{a.tasks_completed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section data-testid="supervisor-tasks-section">
        <h2 className="text-base font-bold text-slate-800 mb-2">Tasks due today / overdue <span className="text-xs font-normal text-slate-400">({data.open_tasks.length})</span></h2>
        {data.open_tasks.length === 0 ? (
          <p className="text-sm text-slate-400 bg-slate-50 rounded-lg px-4 py-4 text-center">No open tasks due.</p>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            {data.open_tasks.map((t) => (
              <div key={t.id} className="px-4 py-2 text-sm flex items-center gap-3 flex-wrap" data-testid={`supervisor-task-${t.id}`}>
                {t.overdue && <span className="text-[10px] font-bold bg-red-100 text-red-700 rounded px-1.5 py-0.5 inline-flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />OVERDUE</span>}
                <span className="font-semibold">{t.agent_name}</span>
                <span className="font-mono text-slate-500 text-xs">{t.customer_phone || ''}</span>
                <span className="text-xs text-slate-400">due {String(t.due_date || '').slice(0, 10)}</span>
                {t.note && <span className="text-xs text-slate-500 truncate max-w-[280px]" title={t.note}>“{t.note}”</span>}
              </div>
            ))}
          </div>
        )}
      </section>

      <section data-testid="supervisor-activity-section">
        <h2 className="text-base font-bold text-slate-800 mb-2">Actioned today by the team</h2>
        {data.activity_today.length === 0 ? (
          <p className="text-sm text-slate-400 bg-slate-50 rounded-lg px-4 py-4 text-center">No callbacks, SMS or task completions logged yet today.</p>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-slate-400 border-b border-slate-100">
                  <th className="px-4 py-2">Agent</th>
                  <th className="px-4 py-2"><Phone className="w-3.5 h-3.5 inline mr-1" />Callbacks logged</th>
                  <th className="px-4 py-2"><MessageSquare className="w-3.5 h-3.5 inline mr-1" />SMS sent</th>
                  <th className="px-4 py-2"><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Tasks completed</th>
                </tr>
              </thead>
              <tbody>
                {data.activity_today.map((a) => (
                  <tr key={a.agent_id} className="border-b border-slate-50 last:border-0" data-testid={`supervisor-activity-${a.agent_id}`}>
                    <td className="px-4 py-2 font-semibold">{a.agent_name}</td>
                    <td className="px-4 py-2 tabular-nums">{a.callbacks_logged}</td>
                    <td className="px-4 py-2 tabular-nums">{a.sms_sent}</td>
                    <td className="px-4 py-2 tabular-nums">{a.tasks_completed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
